import { action, makeObservable, observable } from "mobx";
import { Case, CaseCategories, CaseOrderBy, CaseStatus } from "./types";
import CaseService from "../../services/CaseService";
import authStore from "../AuthStore";
import caseCategories from "../../utils/caseCategories";
import { UserRoles } from "../AuthStore/types";
import lovStore from "../LovStore";
import configStore from "../ConfigStore";

class CaseStore {
  @observable userCases: Case[] = [];
  @observable caseCategories: CaseCategories = {
    mainCategories: [],
    subCategories: {},
  };
  @observable searchKeyword: string = "";
  @observable updatedCases: { id: string; status: CaseStatus }[] = [];
  @observable caseSearchResult: Case[] = [];
  @observable delegationSearchResult: Case[] = [];
  @observable presetCaseItem: Case | null = null;

  caseService: CaseService;

  constructor() {
    makeObservable(this);
    this.caseService = new CaseService();
  }

  @action fetchCaseCategories = async () => {
    const localCache = JSON.parse(
      localStorage.getItem("caseCategories") || "{}",
    ) as CaseCategories;

    if (
      localCache?.lastUpdated &&
      localCache?.lastUpdated >= configStore.appConfig.caseCategoriesUpdatedAt
    ) {
      this.caseCategories = localCache;
    } else {
      return lovStore
        .fetchLovByCode("case-categories-all", "case-categories")
        .then((lov) => {
          if (lov?.value) {
            this.caseCategories = lov.value as CaseCategories;
            localStorage.setItem(
              "caseCategories",
              JSON.stringify({
                ...this.caseCategories,
                lastUpdated: new Date(lov.updatedDate).getTime(),
              }),
            );
          } else {
            this.caseCategories = caseCategories;
            console.log("Using default categories");
          }
        })
        .catch(() => {
          this.caseCategories = caseCategories;
        });
    }
  };

  @action async fetchUserCases() {
    this.userCases = await this.caseService.getCases({
      userId: authStore.user?.id,
    });
  }

  async fetchDelegationCases(params?: {
    [key: string]: string;
  }): Promise<Case[]> {
    return this.caseService
      .searchCases(
        "",
        CaseOrderBy.CREATE_DATE_ASC,
        0,
        parseInt(params?.size || "0"),
        CaseStatus.ACTIVE,
        UserRoles.LAWYER,
      )
      .then((result) => result.cases);
  }

  @action setSearchKeyword(keyword: string) {
    this.searchKeyword = keyword;
  }

  fetchCase(id: string): Promise<Case> {
    return this.caseService.getCase(id);
  }

  searchCases(
    orderBy: CaseOrderBy = CaseOrderBy.CREATE_DATE_ASC,
    page: number = 0,
    size: number = 10,
    status?: CaseStatus,
    role?: UserRoles,
  ): Promise<{ cases: Case[]; count: number }> {
    return new Promise(async (resolve) => {
      const result = await this.caseService.searchCases(
        this.searchKeyword,
        orderBy,
        page,
        size,
        status,
        role,
      );

      // Put a synthetic delay to prevent overload
      setTimeout(() => resolve(result), 500);
    });
  }

  createCase(data: Case): Promise<void> {
    data.ownerId = authStore.user.id;
    console.log(data, authStore.user);

    return this.caseService.createCase(data);
  }

  @action async updateCase(data: Case, id: string): Promise<Case> {
    return this.caseService.updateCase(data, id).then((result) => {
      this.updatedCases = [{ id, status: data.status }];
      return result;
    });
  }

  @action async updateStatus(
    cases: string[],
    status: CaseStatus,
    rejectReason?: string,
  ): Promise<void> {
    return this.caseService
      .updateStatus(cases, status, rejectReason)
      .then((result) => {
        this.updatedCases = cases.map((id) => ({ id, status }));
        return result;
      });
  }

  async deactivateCase(caseId: string): Promise<void> {
    return this.caseService.deactivateCase(caseId);
  }
}

export default CaseStore;
